import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ApiCall from '../utils/apicall';

import { showSuccessToast, showErrorToast } from '../utils/Toast';

// The server's own refusal text (e.g. "Inventory item already exists." or a validation message such as
// "shape Invalid shape type."), not axios's generic "Request failed with status code 400".
const serverMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;

export const getInventory = createAsyncThunk('getInventory/Inventory', async (ThunkApi) => {
  try {
    const res = await ApiCall.get('/inventory');
    return res.data.data.inventories;
  } catch (error) {
    console.log(error);
    return ThunkApi.rejectWithValue(error.message);
  }
});

export const createInventory = createAsyncThunk('createInventory/Inventory', async (data, ThunkApi) => {
  try {
    const fData = new FormData();
    fData.append('ediStdNomenclature', data.ediStdNomenclature);
    fData.append('aiscManualLabel', data.aiscManualLabel);
    fData.append('shape', data.shape);
    fData.append('weight', Number(data.weight));
    fData.append('depth', Number(data.depth));
    fData.append('grade', data.grade);
    fData.append('poNumber', data.poNumber);
    fData.append('heatNumber', data.heatNumber);
    fData.append('itemType', data.itemType);
    fData.append('lengthReceivedFoot', Number(data.lengthReceivedFoot));
    fData.append('lengthReceivedInch', Number(data.lengthReceivedInch));
    fData.append('quantity', Number(data.quantity));
    fData.append('poPulledFromNumber', data.poPulledFromNumber);
    fData.append('lengthUsedFoot', Number(data.lengthUsedFoot));
    fData.append('lengthUsedInch', Number(data.lengthUsedInch));
    fData.append('lengthRemainingFoot', Number(data.lengthRemainingFoot));
    fData.append('lengthRemainingInch', Number(data.lengthRemainingInch));
    fData.append('orderArrivedInFull', data.orderArrivedInFull);
    fData.append('orderArrivedCMTR', data.orderArrivedCMTR);

    const res = await ApiCall.post('/inventory', fData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (res.data.success) {
      return res.data.data.inventory;
    } else {
      return ThunkApi.rejectWithValue(res.data.message || 'Inventory item was not created.');
    }
  } catch (error) {
    console.log(error);
    return ThunkApi.rejectWithValue(serverMessage(error, 'Inventory item was not created.'));
  }
});

export const updateInventory = createAsyncThunk('updateInventorys/Inventory', async (data, ThunkApi) => {
  try {
    const fData = {
      ediStdNomenclature: data.ediStdNomenclature,
      aiscManualLabel: data.aiscManualLabel,
      shape: data.shape,
      weight: Number(data.weight),
      depth: Number(data.depth),
      grade: data.grade,
      poNumber: data.poNumber,
      heatNumber: data.heatNumber,
      itemType: data.itemType,
      lengthReceivedFoot: Number(data.lengthReceivedFoot),
      lengthReceivedInch: Number(data.lengthReceivedInch),
      quantity: Number(data.quantity),
      poPulledFromNumber: data.poPulledFromNumber,
      lengthUsedFoot: Number(data.lengthUsedFoot),
      lengthUsedInch: Number(data.lengthUsedInch),
      lengthRemainingFoot: Number(data.lengthRemainingFoot),
      lengthRemainingInch: Number(data.lengthRemainingInch),
      orderArrivedInFull: data.orderArrivedInFull,
      orderArrivedCMTR: data.orderArrivedCMTR,
    };
    const res = await ApiCall.put(`/inventory/${data.id}`, fData);
    if (res.data?.success && res.data?.data?.inventory) return res.data.data.inventory;
    // A reply without the saved item is not a success (the list would otherwise be updated with nothing).
    return ThunkApi.rejectWithValue(res.data?.message || 'Inventory item was not updated.');
  } catch (error) {
    console.log(error);
    return ThunkApi.rejectWithValue(serverMessage(error, 'Inventory item was not updated.'));
  }
});

export const deleteInventory = createAsyncThunk('deleteInventory/Inventory', async (data, ThunkApi) => {
  try {
    const res = await ApiCall.delete(`/inventory/${data}`);
    if (res.data?.success) {
      return data;
    }
    // Codex WARNING fix: explicit reject when server replies 200 but
    // success=false, so UI doesn't show fake delete success.
    return ThunkApi.rejectWithValue(res.data?.message || "Server reported failure");
  } catch (error) {
    return ThunkApi.rejectWithValue(
      error?.response?.data?.message || error?.message || "Failed to delete inventory item"
    );
  }
});

export const inventorySlice = createSlice({
  name: 'inventory',
  initialState: {
    inventory: [],
    isInventoryLoading: false,
    isSuccess: false,
  },
  extraReducers: (builder) => {
    builder.addCase(getInventory.pending, (state, action) => {
      state.isInventoryLoading = true;
      state.isSuccess = false;
    });
    builder.addCase(getInventory.fulfilled, (state, action) => {
      if (action.payload) {
        state.inventory = action.payload;
      }
      state.isInventoryLoading = false;
      state.isSuccess = true;
    });
    builder.addCase(getInventory.rejected, (state, action) => {
      state.isInventoryLoading = false;
      state.isSuccess = false;
      showErrorToast('Failed to load inventory. Please refresh.');
    });
    // ------------------ Delete Inventory -------------
    builder.addCase(deleteInventory.pending, (state, action) => {
      state.isInventoryLoading = true;
    });
    builder.addCase(deleteInventory.fulfilled, (state, action) => {
      state.inventory = state.inventory.filter((inv) => inv.id !== action.payload);
      state.isInventoryLoading = false;
      showSuccessToast('Inventory item deleted successfully.');
    });
    builder.addCase(deleteInventory.rejected, (state, action) => {
      state.isInventoryLoading = false;
      showErrorToast('Failed to delete inventory item. Please try again.');
    });
    // ------------- Create Inventory ----------------
    builder.addCase(createInventory.pending, (state, action) => {
      state.isInventoryLoading = true;
    });
    builder.addCase(createInventory.fulfilled, (state, action) => {
      if (action.payload) state.inventory.unshift(action.payload);
      state.isInventoryLoading = false;
      showSuccessToast('Inventory item created successfully.');
    });
    builder.addCase(createInventory.rejected, (state, action) => {
      state.isInventoryLoading = false;
      showErrorToast(`Inventory item not created: ${action.payload || action.error?.message || 'unknown error'}`);
    });
    builder.addCase(updateInventory.pending, (state, action) => {
      state.isInventoryLoading = true;
    });
    builder.addCase(updateInventory.fulfilled, (state, action) => {
      const updatedInventory = action.payload;
      state.inventory = state.inventory.map((inv) => {
        if (inv.id === updatedInventory.id) {
          return updatedInventory;
        }
        return inv;
      });
      state.isInventoryLoading = false;
      showSuccessToast('Inventory item updated successfully.');
    });
    builder.addCase(updateInventory.rejected, (state, action) => {
      state.isInventoryLoading = false;
      showErrorToast(`Inventory item not updated: ${action.payload || action.error?.message || 'unknown error'}`);
    });
  },
});

export default inventorySlice.reducer;
